import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, useSlotRecipe} from '@chakra-ui/react3'
import {useIntl} from 'react-intl'
import NavigationLink from '../../navigation-link'
import NavigationSeparator from '../../navigation-separator'
import NavigationHide from '../../hide'
import NavigationTeaser, {hasNavigationTeaserContent} from '../../navigation-teaser'
import FlyoutNavigationLevel3Breadcrumb from './flyout-navigation-level3-breadcrumb'
import {flyoutNavigationLevel3Recipe} from './flyout-navigation-level3.recipe'
import {itemIndex, withStaggerIndices} from './flyout-stagger'
import {trackNavigationClick} from '../../../../../tracking/analytics'
import Link from '../../../link'
import {normalizeContentLink} from '../../../../../utils/url'
import RichText from '../../../cms/rich-text'

/** The authored type rendered by this standard level-3 pane. */
export const NAVIGATION_LEVEL3 = 'navigationLevel3'

/** The entry types level 3 discriminates on, keyed by `type`. */
const NAV_LVL_3_MAP = {
    linkLevel3: NavigationLink,
    navigationSeparator: NavigationSeparator
}

/** Entries whose type has no component render nothing at all. */
const isRenderableLevel3 = (item) => Boolean(NAV_LVL_3_MAP[item.type])

/**
 * Level-3 pane of the flyout navigation.
 *
 * The standard subtree renders three authored columns under a breadcrumb.
 * Three shape traps this level carries, and no other:
 * - `elements[]` entries are **bare** - the `{item: ...}` wrapper stops at
 *   level 2.
 * - separators nest their config under a `separator` sub-object, where levels 1
 *   and 2 carry `variant`/`text` flat.
 * - `moreElements[]` entries carry **no `type`** and no `variant`; they are
 *   always plain links, with `accentColor` flat on the entry.
 *
 * Level 3 arrives embedded in the level-2 payload as `nextLevel`; there is no
 * third fetch and no `leap-navigation-level3` module.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.nextLevel - The level-3 subtree carried by the active
 *   level-2 entry.
 * @param {string} [props.title] - Title of the level-2 entry it was reached
 *   from, shown as the trailing crumb.
 * @param {string} [props.parentTitle] - Title of the level-1 entry the pane
 *   hangs off, shown as the leading crumb on desktop only.
 * @param {Function} props.onBack - Returns to the level-2 pane.
 * @returns {JSX.Element|null}
 */
const FlyoutNavigationLevel3 = ({nextLevel, title, parentTitle, onBack}) => {
    const recipe = useSlotRecipe({recipe: flyoutNavigationLevel3Recipe})
    const styles = useMemo(() => recipe(), [recipe])
    const {formatMessage} = useIntl()

    const {mainTeaser, elements = [], moreElements = [], teaser = []} = nextLevel || {}

    // Both teaser slots are optional, but the CMS template emits `mainTeaser`
    // whether or not one was authored, so presence says nothing (STOK-17335).
    // Dropping the unauthored ones here rather than inside `NavigationTeaser`
    // takes the "Discover" heading and the column with them - the columns carry
    // `_empty: {display: 'none'}`. Derived above the early return because these
    // are hooks, and read only below it.
    const hasMainTeaser = useMemo(() => hasNavigationTeaserContent(mainTeaser), [mainTeaser])
    const teasers = useMemo(() => teaser.filter(hasNavigationTeaserContent), [teaser])
    // Renderable rows paired with their place in the staggered reveal. Each list
    // counts from its own column, so the "Shop" and "More" cascades run in
    // parallel rather than one continuing the other.
    const staggeredElements = useMemo(
        () => withStaggerIndices(elements, isRenderableLevel3),
        [elements]
    )
    const staggeredMoreElements = useMemo(() => withStaggerIndices(moreElements), [moreElements])

    if (nextLevel?.type !== NAVIGATION_LEVEL3) return null

    return (
        <Box css={styles.root}>
            <FlyoutNavigationLevel3Breadcrumb
                title={title}
                parentTitle={parentTitle}
                onBack={onBack}
            />
            <Box css={styles.columns}>
                <Box css={styles.discover}>
                    {hasMainTeaser && (
                        <NavigationHide hide={mainTeaser.hide}>
                            <Text as="p" data-part="heading" css={styles.heading}>
                                {formatMessage({
                                    id: 'leap.navigation.level3.discover',
                                    defaultMessage: 'Discover'
                                })}
                            </Text>
                            <NavigationTeaser {...mainTeaser} variant="large" />
                        </NavigationHide>
                    )}
                </Box>
                <Box css={styles.shop}>
                    {elements.length > 0 && (
                        <>
                            <Text as="p" data-part="heading" css={styles.heading}>
                                {formatMessage({
                                    id: 'leap.navigation.level3.shop',
                                    defaultMessage: 'Shop'
                                })}
                            </Text>
                            <Box
                                as="ul"
                                css={styles.items}
                                aria-label={formatMessage(
                                    {
                                        id: 'leap.navigation.level3.items',
                                        defaultMessage: '{title} navigation'
                                    },
                                    {title}
                                )}
                            >
                                {staggeredElements.map(({item, mobile, desktop}, idx) => {
                                    const Component = NAV_LVL_3_MAP[item.type]

                                    // Props resolve by type, like the component itself:
                                    // a separator passes its nested CMS shape through
                                    // and `NavigationSeparator` resolves it; a link passes
                                    // its own fields plus the CMS `variant` object as
                                    // authored, which is what `NavigationLink` renders
                                    // their card image/copy/price and accent colour from.
                                    const props =
                                        item.type === 'navigationSeparator'
                                            ? item
                                            : {
                                                  title: item.title,
                                                  link: item.link,
                                                  variant: item.variant,
                                                  // Navigates straight to its
                                                  // target — same as the more-links
                                                  // beside it.
                                                  onClick: () => trackNavigationClick(3, item.title)
                                              }
                                    return (
                                        <NavigationHide asChild hide={item.hide} key={idx}>
                                            <Box
                                                as="li"
                                                css={styles.item}
                                                style={itemIndex({mobile, desktop})}
                                            >
                                                <Component {...props} />
                                            </Box>
                                        </NavigationHide>
                                    )
                                })}
                            </Box>
                        </>
                    )}
                </Box>
                <Box css={styles.more}>
                    {moreElements.length > 0 && (
                        <Box css={styles.moreGroup}>
                            <Text as="p" data-part="heading" css={styles.heading}>
                                {formatMessage({
                                    id: 'leap.navigation.level3.more',
                                    defaultMessage: 'More'
                                })}
                            </Text>
                            <Box
                                as="ul"
                                css={styles.moreItems}
                                aria-label={formatMessage(
                                    {
                                        id: 'leap.navigation.level3.moreItems',
                                        defaultMessage: 'More about {title}'
                                    },
                                    {title}
                                )}
                            >
                                {staggeredMoreElements.map(({item, mobile, desktop}, idx) => (
                                    <NavigationHide asChild hide={item.hide} key={idx}>
                                        <Box
                                            as="li"
                                            css={styles.item}
                                            style={itemIndex({mobile, desktop})}
                                        >
                                            {/* More entries are plain links with a flat CMS
                                                link value. They navigate directly and render
                                                without a drill-in chevron. */}
                                            <Link
                                                variant="secondary"
                                                size="small"
                                                href={normalizeContentLink(item.link?.value)}
                                                onClick={() => trackNavigationClick(4, item.title)}
                                            >
                                                <RichText as="span" css={styles.linkText}>
                                                    {item.title}
                                                </RichText>
                                            </Link>
                                        </Box>
                                    </NavigationHide>
                                ))}
                            </Box>
                        </Box>
                    )}
                    {teasers.length > 0 && (
                        <Box css={styles.teasers}>
                            {teasers.map((item, idx) => (
                                <NavigationHide hide={item.hide} key={idx}>
                                    <NavigationTeaser
                                        {...item}
                                        variant={teasers.length === 2 ? 'landscape' : 'small'}
                                    />
                                </NavigationHide>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

FlyoutNavigationLevel3.propTypes = {
    nextLevel: PropTypes.object.isRequired,
    title: PropTypes.string,
    parentTitle: PropTypes.string,
    onBack: PropTypes.func.isRequired
}

if (process.env.NODE_ENV !== 'production') {
    FlyoutNavigationLevel3.displayName = 'FlyoutNavigationLevel3'
}

export default FlyoutNavigationLevel3
